import React, { useEffect, useState } from 'react';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { WORKUP_REPORT_TYPES } from '../../../../common/mappings.js';
import { usePersistedDataStore } from '../../../store.js';

const colors = {
  bgPrimary: '#ffffff',
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
};

const SectionLabel = ({ children }) => (
  <Typography
    component="p"
    sx={{
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: colors.textTertiary,
      m: '0 0 8px',
    }}
  >
    {children}
  </Typography>
);

const WorkupTypeCard = ({ active, disabled, label, onClick }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    role={disabled ? undefined : 'button'}
    tabIndex={disabled ? undefined : 0}
    onKeyDown={
      disabled
        ? undefined
        : (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
    }
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      border: '1.5px solid',
      borderColor: active ? colors.accent : colors.borderTertiary,
      borderRadius: '8px',
      p: '10px 14px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'border-color 0.15s, background 0.15s',
      background: active ? colors.accentBg : colors.bgPrimary,
      opacity: disabled ? 0.55 : 1,
      '&:hover': disabled ? {} : { borderColor: active ? colors.accent : colors.borderSecondary },
    }}
  >
    <PictureAsPdfOutlinedIcon
      sx={{ fontSize: 18, color: active ? colors.accent : colors.textSecondary, flexShrink: 0 }}
    />
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: 0 }}>
      {label}
    </Typography>
  </Box>
);

const CombineSwitch = ({ checked, onChange, disabled = false }) => (
  <Switch
    size="small"
    checked={checked}
    onChange={onChange}
    disabled={disabled}
    sx={{
      width: 32,
      height: 18,
      p: 0,
      flexShrink: 0,
      '& .MuiSwitch-switchBase': {
        p: 0,
        m: '3px',
        transitionDuration: '150ms',
        '&.Mui-checked': {
          transform: 'translateX(14px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            backgroundColor: colors.accent,
            opacity: 1,
          },
        },
      },
      '& .MuiSwitch-thumb': { width: 12, height: 12 },
      '& .MuiSwitch-track': {
        borderRadius: 9,
        backgroundColor: colors.borderSecondary,
        opacity: 1,
      },
    }}
  />
);

const ToggleRow = ({ checked, onChange, disabled, title, description }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      border: `0.5px solid ${colors.borderTertiary}`,
      borderRadius: '8px',
      p: '12px 14px',
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <CombineSwitch checked={checked} onChange={onChange} disabled={disabled} />
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

const DownloadWorkupsDialog = ({ open, onClose, onConfirm }) => {
  const reportType = usePersistedDataStore((state) => state.negotiationObj?.reportType);
  const [workupReportType, setWorkupReportType] = useState('fnmaSummary');
  const [combinePdfs, setCombinePdfs] = useState(true);
  const [includeMaps, setIncludeMaps] = useState(true);

  const isOnePager = workupReportType === 'onepagermaps';

  useEffect(() => {
    if (open) {
      setWorkupReportType(reportType === 'assessment' ? 'onepagermaps' : 'fnmaSummary');
      setCombinePdfs(true);
      setIncludeMaps(true);
    }
  }, [open, reportType]);

  const handleContinue = async () => {
    onClose();
    const resolvedWorkupReportType =
      workupReportType === 'onepagermaps' && !includeMaps ? 'onepager' : workupReportType;
    await onConfirm({ workupReportType: resolvedWorkupReportType, combinePdfs });
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 460,
          borderRadius: '16px',
          border: `0.5px solid ${colors.borderSecondary}`,
          bgcolor: colors.bgPrimary,
          m: 2,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          component="h2"
          sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}
        >
          Download workups
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: '0 0 20px' }}>
          Choose which workup type to generate and download
        </Typography>

        <SectionLabel>Workup type</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
          {WORKUP_REPORT_TYPES.filter((r) => !r.hideFromUi).map((r) => (
            <WorkupTypeCard
              key={r.id}
              active={workupReportType === r.id}
              disabled={reportType === 'assessment' && r.id === 'fnmaSummary'}
              label={r.label}
              onClick={() => setWorkupReportType(r.id)}
            />
          ))}
        </Box>

        <SectionLabel>Download options</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {isOnePager && (
            <ToggleRow
              checked={includeMaps}
              onChange={(e) => setIncludeMaps(e.target.checked)}
              title="Include Maps"
              description={
                includeMaps
                  ? 'Generate one pager with comp location maps'
                  : 'Generate one pager without comp location maps'
              }
            />
          )}
          <ToggleRow
            checked={combinePdfs}
            onChange={(e) => setCombinePdfs(e.target.checked)}
            title="Combine PDFs"
            description={
              combinePdfs
                ? 'Merge all workups into a single PDF file'
                : 'Download each workup as a separate PDF in a zip file, named by parcel ID'
            }
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            pt: 2.5,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: colors.textSecondary,
              border: `0.5px solid ${colors.borderSecondary}`,
              borderRadius: '8px',
              px: 2,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            variant="contained"
            disableElevation
            sx={{
              fontSize: 13,
              fontWeight: 500,
              bgcolor: colors.accent,
              borderRadius: '8px',
              px: 2.5,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
              '&:hover': { bgcolor: '#144f8a' },
            }}
          >
            Download
          </Button>
        </Box>
      </Box>
    </StableMuiDialog>
  );
};

export default DownloadWorkupsDialog;
